import React, { createContext, useContext } from 'react';
import { hasAllPermissions, hasAnyPermission } from '@nwm-oss/auth-core/permissions';
import type { WorkspaceRuntimeMode } from './runtime.js';

export interface FrontendAuthIdentity {
  readonly subjectId: string;
  readonly displayName?: string;
  readonly email?: string;
  readonly username?: string;
  /** Organization from the JWT claim (may differ from activeOrgId when user switches org mid-session). */
  readonly organizationId?: string;
  /** Organization whose grants are reflected in the `permissions` array. */
  readonly activeOrgId?: string;
  /** Human-readable name of the active organization when supplied by the host shell. */
  readonly organizationName?: string;
  readonly roles: readonly string[];
  readonly permissions: readonly string[];
  readonly codeDeclarant?: string | null;
  readonly profileCode?: string;
}

export interface FrontendAuthSession {
  readonly mode: 'oidc' | 'mock' | 'embedded';
  readonly runtime: WorkspaceRuntimeMode;
  readonly identity: FrontendAuthIdentity | null;
  readonly isAuthenticated: boolean;
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly accessToken: string | null;
  readonly login?: (returnTo?: string) => Promise<void>;
  readonly logout?: () => Promise<void>;
  readonly handleCallback?: () => Promise<string | null>;
}

const FrontendAuthContext = createContext<FrontendAuthSession | null>(null);

export function FrontendAuthProvider({
  children,
  value,
}: {
  readonly children: React.ReactNode;
  readonly value: FrontendAuthSession;
}) {
  return <FrontendAuthContext.Provider value={value}>{children}</FrontendAuthContext.Provider>;
}

export function useFrontendAuth(): FrontendAuthSession {
  const context = useContext(FrontendAuthContext);
  if (!context) {
    throw new Error('useFrontendAuth must be used inside <FrontendAuthProvider>');
  }
  return context;
}

export function useHasPermission(permission: string): boolean {
  const { identity } = useFrontendAuth();
  return identity ? hasAnyPermission(identity.permissions, [permission]) : false;
}

export function useHasAnyPermissions(permissions: readonly string[]): boolean {
  const { identity } = useFrontendAuth();
  return identity ? hasAnyPermission(identity.permissions, permissions) : false;
}

export function useHasAllPermissions(permissions: readonly string[]): boolean {
  const { identity } = useFrontendAuth();
  return identity ? hasAllPermissions(identity.permissions, permissions) : false;
}

export function PermissionGate({
  allOf,
  anyOf,
  children,
  fallback = null,
}: {
  readonly allOf?: readonly string[];
  readonly anyOf?: readonly string[];
  readonly children: React.ReactNode;
  readonly fallback?: React.ReactNode;
}) {
  const { identity } = useFrontendAuth();
  const grantedPermissions = identity?.permissions ?? [];

  const allOfSatisfied = !allOf || hasAllPermissions(grantedPermissions, allOf);
  const anyOfSatisfied = !anyOf || hasAnyPermission(grantedPermissions, anyOf);

  if (allOfSatisfied && anyOfSatisfied) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}
